import {
  UH_CONSENT_COOKIE,
  parseConsent,
  type ConsentRecord,
} from 'lib/consent/record';
import { allowsAds, getConsentGate } from 'lib/consent/gate';
import { isAdClickId } from 'lib/analytics/forwardedParams';
import { rememberLandingParams } from 'lib/analytics/landingParams';

/** Allowlisted keys copied onto a Shopify checkout, not onto ordinary links. */
export const CHECKOUT_HANDOFF_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'affiliate',
  'affiliateCode',
  'referral',
  'discount',
  'sscid',
  'irclickid',
  'subId1',
  'subId2',
  'subId3',
  'rvo_aff',
  'click_id',
  'flow',
  'gclid',
  'fbclid',
  'gbraid',
  'wbraid',
  'gclsrc',
  'gad_source',
  'gad_campaignid',
  'ttclid',
  'fbadid',
  'tw_source',
  'tw_adid',
  'tw_campaign',
  'rdt_cid',
] as const;

export const UH_CONSENT_ATTRIBUTE = '_uh_consent';

const META_MATCH_KEYS = new Set(['_fbp', '_fbc', '_uh_fbclid']);

/** Ad click ids, Google Ads `hsa_*` params, and Meta match cookies. */
export function isAdTrackingParam(key: string): boolean {
  return isAdClickId(key) || key.startsWith('hsa_') || META_MATCH_KEYS.has(key);
}

/**
 * Cart attributes safe to write for this consent.
 * Email is never stored. Ad tracking keys are omitted, and removed from the
 * existing cart, until ads consent is on. `shouldWrite` is true when the
 * URL has something to add or the cart still holds a key that must be dropped.
 */
export function cartAttributesForSync(
  existing: ReadonlyArray<{ key: string; value: string }> | undefined,
  incoming: ReadonlyArray<{ key: string; value: string }> | undefined,
  adsAllowed: boolean,
): { attributes: { key: string; value: string }[]; shouldWrite: boolean } {
  const drop = (key: string) => key === 'email' || (!adsAllowed && isAdTrackingParam(key));
  const keptExisting = (existing ?? []).filter((attr) => attr.value && !drop(attr.key));
  const keptIncoming = (incoming ?? []).filter((attr) => attr.value && !drop(attr.key));
  const stripped = (existing ?? []).some((attr) => drop(attr.key));
  if (!stripped && keptIncoming.length === 0) {
    return { attributes: keptExisting, shouldWrite: false };
  }
  const merged = new Map<string, string>();
  for (const attr of keptExisting) merged.set(attr.key, attr.value);
  for (const attr of keptIncoming) merged.set(attr.key, attr.value);
  return {
    attributes: Array.from(merged, ([key, value]) => ({ key, value })),
    shouldWrite: true,
  };
}

/** Remove ad tracking params, and any `attributes[email]`, from a checkout URL. */
export function stripHeldCheckoutParams(url: URL, adsAllowed: boolean): void {
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('attributes[') && key.endsWith(']')) {
      const inner = key.slice('attributes['.length, -1);
      if (inner === 'email' || (!adsAllowed && isAdTrackingParam(inner))) {
        url.searchParams.delete(key);
      }
      continue;
    }
    if (!adsAllowed && isAdTrackingParam(key)) url.searchParams.delete(key);
  }
}

/**
 * `<choice>[:gpc][;live=SAM]`. `live` is what this page actually allowed
 * (statistics, analytics, marketing), so a `none` in an opt-out country and a
 * `none` in an opt-in one stay distinguishable on the order.
 */
export function consentAttributeValue(
  record: ConsentRecord | null,
  gpc = false,
  live?: { stats: boolean; analytics: boolean; ads: boolean },
): string {
  let base = 'none';
  if (record) {
    if (record.choice !== 'custom' || !record.categories) base = record.choice;
    else {
      const categories = record.categories;
      base = `custom:${categories.stats ? 1 : 0}${categories.analytics ? 1 : 0}${categories.ads ? 1 : 0}`;
    }
  }
  const withGpc = gpc || record?.gpc === true ? `${base}:gpc` : base;
  if (!live) return withGpc;
  const bit = (on: boolean) => (on ? 1 : 0);
  return `${withGpc};live=${bit(live.stats)}${bit(live.analytics)}${bit(live.ads)}`;
}

function liveGpc(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

export function readConsentAttribute(): string {
  const live = getConsentGate();
  if (typeof document === 'undefined') return consentAttributeValue(null, liveGpc(), live);
  const row = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${UH_CONSENT_COOKIE}=`));
  if (!row) return consentAttributeValue(null, liveGpc(), live);
  try {
    const record = parseConsent(
      decodeURIComponent(row.slice(UH_CONSENT_COOKIE.length + 1)),
    );
    return consentAttributeValue(record, liveGpc(), live);
  } catch {
    return consentAttributeValue(null, liveGpc(), live);
  }
}

function handoffValue(
  key: (typeof CHECKOUT_HANDOFF_KEYS)[number],
  params: URLSearchParams,
  landing: ReturnType<typeof rememberLandingParams>,
): string | null {
  return params.get(key) || landing[key as keyof typeof landing] || null;
}

/** Writes allowlisted campaign and affiliate params, plus `_uh_consent`. Email is never copied. */
export function applyCheckoutHandoff(url: URL, search = ''): void {
  const params = new URLSearchParams(search);
  // Includes click ids held in memory; the ads gate below still drops them.
  const landing = rememberLandingParams(search);
  const ads = allowsAds();
  for (const key of CHECKOUT_HANDOFF_KEYS) {
    if (!ads && isAdClickId(key)) continue;
    const value = handoffValue(key, params, landing);
    if (!value) continue;
    if (key === 'discount') {
      if (!url.searchParams.get('discount')) url.searchParams.set('discount', value);
      continue;
    }
    const attr = `attributes[${key}]`;
    if (!url.searchParams.get(attr)) url.searchParams.set(attr, value);
  }
  url.searchParams.set(
    `attributes[${UH_CONSENT_ATTRIBUTE}]`,
    readConsentAttribute(),
  );
}
